import React, { useCallback, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	OutlinedInput,
	Select,
	Stack,
	Tooltip,
} from "@mui/material";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { XIcon } from "@phosphor-icons/react/dist/ssr/X";
import { Controller, useForm } from "react-hook-form";
import { z as zod } from "zod";

import { keyVaultKeys, useCreateKeyVault, type KeyVault } from "@/hooks/api/use-key-vault";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";

import { Option } from "../core/option";
import { TextAreaWithExpand } from "./text-area-with-field";

const schema = zod.object({
	label: zod.string().min(1, "Label is required"),
	value: zod.string().min(1, "Value is required"),
});

type FormValues = zod.infer<typeof schema>;

export function SecretField({
	field,
	dynamicField,
	onChange,
	label,
}: {
	field: any;
	dynamicField: any;
	onChange: (value: string) => void;
	label: string;
}) {
	const [page, setPage] = useState(1);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const {
		items: keyVaults,
		isLoading,
		totalItems,
		refetchData: fetchKeyVaults,
	} = useCachedTableStateWithUrl<KeyVault>({
		queryKey: keyVaultKeys.lists(),
		endpoint: "/key-vault",
	});

	const createKeyVaultMutation = useCreateKeyVault();

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			label: "",
			value: "",
		},
	});

	const fetchOptions = useCallback(
		async (pageNum: number) => {
			try {
				setPage(pageNum);
				const limit = 50;
				const offset = (pageNum - 1) * limit;
				const searchParams = `limit=${limit}&offset=${offset}&scope=${dynamicField.scope}`;

				fetchKeyVaults(searchParams);
			} catch (error) {
				console.error(error);
			}
		},
		[dynamicField.scope, fetchKeyVaults]
	);

	useEffect(() => {
		fetchOptions(1);
	}, [fetchOptions]);

	const handleOpenModal = () => {
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		reset();
	};

	const onSubmitForm = async (values: FormValues) => {
		try {
			const newKeyVault = await createKeyVaultMutation.mutateAsync({
				label: values.label,
				value: values.value,
				scope: dynamicField.scope,
			});

			// Refetch options to include the new item
			await fetchOptions(1);

			// Select the newly created item
			onChange?.(newKeyVault.id);
			field.onChange({ target: { value: newKeyVault.id } });

			handleCloseModal();
		} catch (error) {
			console.error(error);
		}
	};

	const isJson = dynamicField.format === "json";

	return (
		<Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mt: 1 }}>
			<Select
				{...field}
				defaultValue={dynamicField.defaultValue || ""}
				MenuProps={{
					PaperProps: {
						style: { maxHeight: 220, fontSize: "16px" },
						onScroll: (event: any) => {
							const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
							const isNearBottom = scrollTop + clientHeight >= scrollHeight - 60;
							if (isNearBottom && !isLoading && totalItems > keyVaults.length) {
								fetchOptions(page + 1);
							}
						},
					},
				}}
				value={isLoading ? "loading" : field.value === undefined ? "" : String(field.value)}
				onChange={(event) => {
					const value = event.target.value as string;
					onChange?.(value);
					field.onChange(event);
				}}
				sx={{
					flex: 1,
					color: field.value === "" || field.value === undefined ? "var(--mui-palette-text-secondary)" : "inherit",
				}}
			>
				<Option
					value=""
					sx={{ color: "var(--mui-palette-text-secondary)", fontStyle: "italic", fontSize: "16px !important" }}
				>
					{dynamicField.placeholder || `Select ${label}`}
				</Option>
				{keyVaults?.map((option: KeyVault) => {
					return (
						<Option key={option.id} value={option.id} sx={{ fontSize: "16px !important" }}>
							{option.label}
						</Option>
					);
				})}
				{isLoading && (
					<Option
						disabled
						value="loading"
						sx={{
							display: "flex",
							justifyContent: "center",
							padding: 1,
							width: "100%",
							marginTop: 1,
							opacity: "1 !important",
						}}
					>
						<CircularProgress size={20} />
					</Option>
				)}
			</Select>

			<Tooltip title={`Add a new ${label}`} arrow placement="top">
				<IconButton
					onClick={handleOpenModal}
					sx={{
						border: "1px solid var(--mui-palette-divider)",
						borderRadius: 1,
					}}
				>
					<PlusIcon />
				</IconButton>
			</Tooltip>

			<Dialog
				open={isModalOpen}
				onClose={handleCloseModal}
				sx={{
					"& .MuiDialog-container": {
						"& .MuiPaper-root": {
							width: "100%",
							maxWidth: "480px",
						},
					},
				}}
			>
				<DialogTitle>
					Add {label}
					<IconButton color="inherit" onClick={handleCloseModal} sx={{ position: "absolute", right: 8, top: 8 }}>
						<XIcon width={20} height={20} />
					</IconButton>
				</DialogTitle>

				<DialogContent>
					<form onSubmit={handleSubmit(onSubmitForm)}>
						<Grid container spacing={2} sx={{ pt: 2, pb: 2 }}>
							<Grid
								size={{
									md: 12,
									xs: 12,
								}}
							>
								<Controller
									control={control}
									name="label"
									render={({ field }) => (
										<FormControl error={Boolean(errors.label)} fullWidth required>
											<InputLabel>Label</InputLabel>
											<OutlinedInput {...field} placeholder="Enter label" />
											{errors.label ? <CustomFormHelperError text={errors.label.message || ""} /> : null}
										</FormControl>
									)}
								/>
							</Grid>
							<Grid
								size={{
									md: 12,
									xs: 12,
								}}
							>
								<Controller
									control={control}
									name="value"
									render={({ field }) => (
										<FormControl error={Boolean(errors.value)} fullWidth required>
											<InputLabel>Value</InputLabel>
											{isJson ? (
												<TextAreaWithExpand
													field={field}
													label={label}
													placeholder={dynamicField.placeholder}
													withFormatJson
												/>
											) : (
												<OutlinedInput {...field} placeholder="Enter value" type="password" />
											)}
											{errors.value ? <CustomFormHelperError text={errors.value.message || ""} /> : null}
										</FormControl>
									)}
								/>
							</Grid>
						</Grid>
					</form>
				</DialogContent>

				<DialogActions sx={{ borderTop: "1px solid var(--mui-palette-divider)" }}>
					<Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
						<Button color="secondary" onClick={handleCloseModal}>
							Cancel
						</Button>
						<Button
							color="primary"
							variant="contained"
							onClick={handleSubmit(onSubmitForm)}
							disabled={isSubmitting || createKeyVaultMutation.isPending}
						>
							Add
						</Button>
					</Stack>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
